import { motion, AnimatePresence } from "framer-motion";
import { HelpCircle } from "lucide-react";
import SceneShell from "../components/SceneShell";

/**
 * Scene · Guess the AI Cost · stage-prop driven.
 *
 *   stage = 0  →  Question + £5 (current) vs pulsing "?" (AI cost)
 *   stage = 1  →  Reveal £0.15 alongside the £5 for comparison
 */

const RED = "#c83a4f";
const GREEN = "#96C11F";
const AQUA = "#00deb6";

export default function SceneGuessAICost({ stage = 0 }) {
  return (
    <SceneShell variant="ink" align="center" data-testid="scene-guess-ai-cost">
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background:
            stage === 0
              ? [
                  "radial-gradient(ellipse at center, rgba(150,193,31,0.10) 0%, transparent 60%)",
                  "radial-gradient(ellipse at center, rgba(0,222,182,0.16) 0%, transparent 60%)",
                  "radial-gradient(ellipse at center, rgba(150,193,31,0.10) 0%, transparent 60%)",
                ]
              : "radial-gradient(ellipse at center, rgba(150,193,31,0.30) 0%, transparent 65%)",
        }}
        transition={{
          duration: stage === 0 ? 8 : 1.2,
          repeat: stage === 0 ? Infinity : 0,
          ease: "easeInOut",
        }}
      />

      <div className="relative max-w-[1500px] mx-auto w-full text-center flex flex-col items-center">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="kn-eyebrow flex items-center gap-3 mx-auto"
          style={{ color: GREEN }}
        >
          <HelpCircle className="w-3.5 h-3.5" />
          Quick guess
        </motion.div>

        {/* Question */}
        <motion.h2
          initial={{ opacity: 0, y: 24, filter: "blur(8px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 kn-display leading-[1.05]"
          style={{
            fontSize: "clamp(40px, 5.6vw, 84px)",
            color: "#e2e8f0",
            fontWeight: 800,
            letterSpacing: "-0.02em",
            maxWidth: "1300px",
          }}
          data-testid="ai-cost-question"
        >
          What's the{" "}
          <span
            style={{
              background: `linear-gradient(135deg, ${GREEN} 0%, ${AQUA} 100%)`,
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              color: "transparent",
            }}
          >
            cost per edit
          </span>{" "}
          using <span style={{ color: GREEN, fontWeight: 900 }}>AI tools</span>?
        </motion.h2>

        {/* Comparison row · current vs ? / answer */}
        <div className="mt-16 flex flex-wrap items-center justify-center gap-6 sm:gap-10">
          {/* Current cost — always visible */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.0, delay: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl px-8 py-7 min-w-[260px]"
            style={{
              background:
                "linear-gradient(135deg, rgba(60,12,22,0.85) 0%, rgba(90,20,32,0.75) 100%)",
              border: `1px solid ${RED}55`,
            }}
          >
            <div
              className="text-[11px] tracking-[0.32em] uppercase mb-2"
              style={{ color: "#ff8597" }}
            >
              Current
            </div>
            <div
              className="kn-display tnum text-[64px] sm:text-[88px] leading-none"
              style={{ color: "#ff8597", fontWeight: 800 }}
            >
              £5
            </div>
            <div
              className="mt-2 text-xs tracking-[0.18em] uppercase"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              per edit · today
            </div>
          </motion.div>

          {/* VS dot */}
          <motion.div
            initial={{ opacity: 0, scale: 0.4 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 1.6 }}
            className="text-3xl sm:text-4xl"
            style={{ color: "rgba(255,255,255,0.45)", fontWeight: 800 }}
          >
            vs
          </motion.div>

          {/* AI cost — ? on stage 0, £0.15 on stage 1 */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.0, delay: 1.8, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-2xl px-8 py-7 min-w-[260px] relative overflow-hidden glass"
            style={{
              boxShadow: "0 0 60px rgba(150,193,31,0.20)",
            }}
            data-testid="ai-cost-answer-box"
          >
            <div
              className="text-[11px] tracking-[0.32em] uppercase mb-2"
              style={{ color: GREEN }}
            >
              With AI
            </div>
            <AnimatePresence mode="wait">
              {stage === 0 ? (
                <motion.div
                  key="q-mark"
                  initial={{ opacity: 0, scale: 0.6 }}
                  animate={{
                    opacity: [0.55, 1, 0.55],
                    scale: 1,
                  }}
                  exit={{ opacity: 0, scale: 0.4, filter: "blur(20px)" }}
                  transition={{
                    opacity: {
                      duration: 2.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    },
                    scale: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
                  }}
                  className="kn-display text-[64px] sm:text-[88px] leading-none"
                  style={{ color: GREEN, fontWeight: 900 }}
                >
                  ?
                </motion.div>
              ) : (
                <motion.div
                  key="answer"
                  initial={{ opacity: 0, scale: 0.6, filter: "blur(20px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
                  className="kn-display tnum text-[64px] sm:text-[88px] leading-none whitespace-nowrap"
                  style={{
                    background: `linear-gradient(135deg, ${GREEN} 0%, ${AQUA} 100%)`,
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    color: "transparent",
                    fontWeight: 900,
                    textShadow: "0 0 60px rgba(150,193,31,0.55)",
                  }}
                  data-testid="ai-cost-answer"
                >
                  £0.15
                </motion.div>
              )}
            </AnimatePresence>
            <div
              className="mt-2 text-xs tracking-[0.18em] uppercase"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              per edit · all-in
            </div>
          </motion.div>
        </div>
      </div>
    </SceneShell>
  );
}
